import React, { useState } from "react";
import { Box, Button, Layer, Heading, Text, Spinner, Notification } from "grommet";
import axios from "../../config/adminAxios";
import API_URL from "../../config/config";
import LogoutButton from "../LogoutButton";

const TEAM_MEMBER_ERROR = "you are part of a manager's team";

const UpgradeToManager = ({ user_id, onUpgrade }) => {
  const [showUpgradePrompt, setShowUpgradePrompt] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isUpgraded, setIsUpgraded] = useState(false);
  const [error, setError] = useState("");

  const openTeamUpgradeEmail = async () => {
    try {
      const response = await fetch("/api/ticket/generate-ticket", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ user_id, type: "Manager Role Upgrade" }),
      });
      if (response.status === 429) {
        const retryAfter = response.headers.get("retry-after");
        const retryAt = retryAfter ? Date.now() + parseInt(retryAfter, 10) * 1000 : null;
        window.dispatchEvent(new CustomEvent("app:rate-limited", { detail: { retryAt } }));
        return;
      }
      const { ticket_number } = response.ok ? await response.json() : { ticket_number: "N/A" };
      const subject = encodeURIComponent(`Request to Upgrade to Manager [Ticket: ${ticket_number}]`);
      const body = encodeURIComponent(
        `Dear Support Team,\n\nI would like to request an upgrade to Manager access on HPE Digital Skill Advisor.\n\nI am currently part of a manager's team and require individual manager access.\n\nTicket Number: ${ticket_number}\n\nThank you.`
      );
      const bcc = encodeURIComponent("hpe.skilladvisor@meteoros.in");
      window.open(`mailto:hpeskill.advisor@hpe.com?subject=${subject}&bcc=${bcc}&body=${body}`);
    } catch {
      window.open("mailto:hpeskill.advisor@hpe.com?subject=Request%20to%20Upgrade%20to%20Manager");
    }
  };

  const handleUpgrade = async () => {
    setIsLoading(true);
    setError("");
    try {
      await axios.get(`${API_URL}/user/upgrade_to_manager/${user_id}`);
      // Don't call onUpgrade() here — it unmounts this component before the
      // success modal can render. The user will get manager access after re-login.
      setIsUpgraded(true);
    } catch (err) {
      const msg = err.response?.data?.message || "";
      if (msg.toLowerCase().includes(TEAM_MEMBER_ERROR)) {
        setShowUpgradePrompt(false);
        openTeamUpgradeEmail();
      } else {
        setError(msg || "Failed to upgrade to Manager. Please try again.");
      }
    } finally {
      setIsLoading(false);
      setShowUpgradePrompt(false);
    }
  };

  return (
    <>
      <Button label="Upgrade to Manager" onClick={() => setShowUpgradePrompt(true)} plain={false} />

      {showUpgradePrompt && (
        <Layer onEsc={() => setShowUpgradePrompt(false)} onClickOutside={() => setShowUpgradePrompt(false)}>
          <Box pad="medium" gap="medium" width="medium">
            <Heading level="3" margin="none">Confirm Upgrade</Heading>
            <Text>Are you sure you want to upgrade to Manager?</Text>
            <Box direction="row" gap="small" justify="end">
              <Button label="Cancel" onClick={() => setShowUpgradePrompt(false)} />
              <Button label="Confirm" primary onClick={handleUpgrade} />
            </Box>
          </Box>
        </Layer>
      )}

      {isLoading && (
        <Layer>
          <Box align="center" justify="center" pad="medium" gap="small">
            <Spinner />
            <Text>Upgrading...</Text>
          </Box>
        </Layer>
      )}

      {isUpgraded && (
        <Layer>
          <Box pad="medium" gap="medium" width="medium" align="center">
            <Heading level="3" margin="none">Upgrade Successful!</Heading>
            <Text textAlign="center">Please log out and log in again to access the Manager portal.</Text>
            <LogoutButton />
          </Box>
        </Layer>
      )}

      {error && (
        <Notification
          toast
          status="critical"
          title="Upgrade Failed"
          message={error}
          onClose={() => setError("")}
        />
      )}
    </>
  );
};

export default UpgradeToManager;
